import {
  parseRuntimeWorldTilePayloadV2,
  type RuntimeTileLodLevel,
  type RuntimeWorldTilePayloadV2,
} from "../world/schema/runtimeTile.ts";
import type { LocalHorizontal } from "../world/coordinates.ts";
import type { HorizontalBounds } from "../world/geometry.ts";
import type { WorldTileId } from "../world/schema/tile.ts";
import type { RuntimeTileInstanceMetadata } from "../world/streaming/preparedMetadata.ts";

export type {
  FullDetailBuildingMetadata,
  ReducedBuildingMetadata,
  RuntimeTileInstanceMetadata,
} from "../world/streaming/preparedMetadata.ts";

export const PREPARE_RUNTIME_TILE_REQUEST = "prepare-runtime-tile" as const;
export const RUNTIME_TILE_READY_RESPONSE = "runtime-tile-ready" as const;
export const RUNTIME_TILE_ERROR_RESPONSE = "runtime-tile-error" as const;

export interface PrepareRuntimeTileRequest {
  type: typeof PREPARE_RUNTIME_TILE_REQUEST;
  requestId: string;
  tileId: WorldTileId;
  lod: RuntimeTileLodLevel;
  json: string;
}

export interface RuntimeTileReadyResponse {
  type: typeof RUNTIME_TILE_READY_RESPONSE;
  requestId: string;
  tileId: WorldTileId;
  lod: RuntimeTileLodLevel;
  /** The verified source text avoids an expensive structured clone of the
   * deeply nested payload back to the main thread. */
  json: string;
  instances: RuntimeTileInstanceMetadata;
  sourceByteLength: number;
}

export interface RuntimeTileErrorResponse {
  type: typeof RUNTIME_TILE_ERROR_RESPONSE;
  requestId: string;
  tileId?: WorldTileId;
  message: string;
}

export type RuntimeTileWorkerResponse =
  | RuntimeTileReadyResponse
  | RuntimeTileErrorResponse;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function requireNonEmptyString(value: unknown, path: string): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(`${path} must be a non-empty string`);
  }
  return value;
}

function requireLod(value: unknown): RuntimeTileLodLevel {
  if (value !== 0 && value !== 1 && value !== 2) {
    throw new Error("request.lod must be 0, 1, or 2");
  }
  return value;
}

export function parsePrepareRuntimeTileRequest(
  value: unknown,
): PrepareRuntimeTileRequest {
  if (!isRecord(value)) {
    throw new Error("worker request must be an object");
  }
  if (value.type !== PREPARE_RUNTIME_TILE_REQUEST) {
    throw new Error(`Unsupported worker request type: ${String(value.type)}`);
  }
  const requestId = requireNonEmptyString(value.requestId, "request.requestId");
  const tileId = requireNonEmptyString(
    value.tileId,
    "request.tileId",
  ) as WorldTileId;
  const json = requireNonEmptyString(value.json, "request.json");
  const lod = requireLod(value.lod);
  return {
    type: PREPARE_RUNTIME_TILE_REQUEST,
    requestId,
    tileId,
    lod,
    json,
  };
}

function boundsForPoints(
  points: readonly Readonly<LocalHorizontal>[],
): HorizontalBounds {
  if (points.length === 0) {
    throw new Error("Building outer ring must not be empty");
  }
  let minX = Number.POSITIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;
  for (const point of points) {
    minX = Math.min(minX, point.x);
    minZ = Math.min(minZ, point.z);
    maxX = Math.max(maxX, point.x);
    maxZ = Math.max(maxZ, point.z);
  }
  return { minX, minZ, maxX, maxZ };
}

function centerOfBounds(bounds: Readonly<HorizontalBounds>): LocalHorizontal {
  return {
    x: (bounds.minX + bounds.maxX) / 2,
    z: (bounds.minZ + bounds.maxZ) / 2,
  };
}

export function prepareRuntimeTileInstanceMetadata(
  payload: Readonly<RuntimeWorldTilePayloadV2>,
  lod: RuntimeTileLodLevel,
): RuntimeTileInstanceMetadata {
  if (lod === 0) {
    return {
      tileId: payload.id,
      lod,
      terrain: {
        samplesPerSide: payload.terrain.samplesPerSide,
        spacingM: payload.terrain.spacingM,
        sampleCount: payload.terrain.heightsM.length,
      },
      buildings: {
        kind: "footprints",
        items: payload.buildings.map((building) => {
          const bounds = boundsForPoints(building.footprint.outer);
          return {
            buildingId: building.id,
            sourceFootprintId: building.sourceFootprintId,
            sourceId: building.sourceId,
            center: centerOfBounds(bounds),
            bounds,
            baseElevationM: building.baseElevationM,
            ...(building.heightM === undefined
              ? {}
              : { heightM: building.heightM }),
            outerVertexCount: building.footprint.outer.length,
            holeCount: building.footprint.holes.length,
            holeVertexCount: building.footprint.holes.reduce(
              (count, hole) => count + hole.length,
              0,
            ),
          };
        }),
      },
      featureCounts: {
        buildings: payload.buildings.length,
        transportLines: payload.transportLines.length,
        waterLines: payload.waterLines.length,
        landCoverMasks: payload.landCoverMasks.length,
        collisionFootprints: payload.collision.buildingFootprints.length,
      },
    };
  }

  const reduced = payload.lods[lod - 1];
  if (reduced === undefined || reduced.level !== lod) {
    throw new Error(`Tile ${payload.id} does not contain LOD ${lod}`);
  }
  return {
    tileId: payload.id,
    lod,
    terrain: {
      samplesPerSide: reduced.terrain.samplesPerSide,
      spacingM: reduced.terrain.spacingM,
      sampleCount: reduced.terrain.heightsM.length,
    },
    buildings: {
      kind: "source-bounds",
      items: reduced.buildingBounds.map((building) => ({
        buildingId: building.buildingId,
        center: centerOfBounds(building.bounds),
        bounds: building.bounds,
        baseElevationM: building.baseElevationM,
      })),
    },
    featureCounts: { ...reduced.featureCounts },
  };
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function handleRuntimeTileWorkerRequest(
  value: unknown,
): RuntimeTileWorkerResponse {
  let requestId = "unknown";
  let tileId: WorldTileId | undefined;
  try {
    if (isRecord(value)) {
      if (typeof value.requestId === "string" && value.requestId.length > 0) {
        requestId = value.requestId;
      }
      if (typeof value.tileId === "string" && value.tileId.length > 0) {
        tileId = value.tileId as WorldTileId;
      }
    }
    const request = parsePrepareRuntimeTileRequest(value);
    const parsedJson: unknown = JSON.parse(request.json);
    const payload = parseRuntimeWorldTilePayloadV2(parsedJson);
    if (payload.id !== request.tileId) {
      throw new Error(
        `Worker tile ID mismatch: requested ${request.tileId}, parsed ${payload.id}`,
      );
    }
    return {
      type: RUNTIME_TILE_READY_RESPONSE,
      requestId: request.requestId,
      tileId: request.tileId,
      lod: request.lod,
      json: request.json,
      instances: prepareRuntimeTileInstanceMetadata(payload, request.lod),
      sourceByteLength: new TextEncoder().encode(request.json).byteLength,
    };
  } catch (error: unknown) {
    return {
      type: RUNTIME_TILE_ERROR_RESPONSE,
      requestId,
      ...(tileId === undefined ? {} : { tileId }),
      message: errorMessage(error),
    };
  }
}
